import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar,
  Users,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Edit,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
  Filter,
  Search,
  MoreVertical,
  RefreshCw,
  FileText,
  Timer,
  TrendingUp,
  TrendingDown,
  Building,
  UserCheck,
  Coffee,
  Sun,
  Moon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Progress } from '../../ui/progress';
import { Checkbox } from '../../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { db } from '../../../lib/db';
import { v4 as uuidv4 } from 'uuid';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const ATTENDANCE_STATUSES = [
  { value: 'present', label: 'Present', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'absent', label: 'Absent', color: 'bg-red-100 text-red-700' },
  { value: 'half_day', label: 'Half Day', color: 'bg-amber-100 text-amber-700' },
  { value: 'leave', label: 'On Leave', color: 'bg-blue-100 text-blue-700' },
  { value: 'holiday', label: 'Holiday', color: 'bg-purple-100 text-purple-700' },
  { value: 'weekend', label: 'Weekend', color: 'bg-gray-100 text-gray-500' }
];

export default function MonthlyAttendanceManagement() {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [showEmployeeDetailModal, setShowEmployeeDetailModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [bulkEditData, setBulkEditData] = useState({
    status: 'present',
    applyToAll: false,
    selectedEmployees: [],
    selectedDates: []
  });

  // Summary stats
  const [summary, setSummary] = useState({
    totalEmployees: 0,
    avgAttendance: 0,
    totalPresent: 0,
    totalAbsent: 0,
    totalLeave: 0,
    totalOvertime: 0
  });

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get all active employees
      const allUsers = await db.users
        .where('status')
        .equals('active')
        .toArray();
      
      const activeEmployees = allUsers.filter(u => 
        u.role !== 'admin' && u.status === 'active'
      );
      setEmployees(activeEmployees);

      // Get attendance records for selected month
      const startOfMonth = new Date(selectedYear, selectedMonth, 1).getTime();
      const endOfMonth = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59).getTime();

      const records = await db.attendanceRecords
        .where('date')
        .between(startOfMonth, endOfMonth)
        .toArray();

      // Organize by employee and date
      const organizedData = {};
      for (const emp of activeEmployees) {
        organizedData[emp.id] = {};
      }

      for (const record of records) {
        if (organizedData[record.userId]) {
          const dateKey = new Date(record.date).toISOString().split('T')[0];
          organizedData[record.userId][dateKey] = record;
        }
      }

      setAttendanceData(organizedData);

      // Calculate summary
      calculateSummary(activeEmployees, organizedData);

    } catch (error) {
      console.error('Error loading attendance data:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (emps, data) => {
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    let totalPresent = 0;
    let totalAbsent = 0;
    let totalLeave = 0;
    let totalOvertime = 0;
    let workingDays = 0;

    // Calculate working days (exclude weekends)
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(selectedYear, selectedMonth, d);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        workingDays++;
      }
    }

    for (const emp of emps) {
      const empData = data[emp.id] || {};
      for (const dateKey of Object.keys(empData)) {
        const record = empData[dateKey];
        if (record.status === 'present') totalPresent++;
        if (record.status === 'absent') totalAbsent++;
        if (record.status === 'leave') totalLeave++;
        totalOvertime += parseFloat(record.overtime || 0);
      }
    }

    const avgAttendance = emps.length > 0 && workingDays > 0
      ? Math.round((totalPresent / (emps.length * workingDays)) * 100)
      : 0;

    setSummary({
      totalEmployees: emps.length,
      avgAttendance,
      totalPresent,
      totalAbsent,
      totalLeave,
      totalOvertime: totalOvertime.toFixed(1),
      workingDays
    });
  };

  const daysInMonth = useMemo(() => {
    return new Date(selectedYear, selectedMonth + 1, 0).getDate();
  }, [selectedMonth, selectedYear]);

  const departments = useMemo(() => {
    const depts = new Set(employees.map(e => e.profile?.department).filter(Boolean));
    return ['all', ...Array.from(depts)];
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      const matchesDept = selectedDepartment === 'all' || 
        emp.profile?.department === selectedDepartment;
      const matchesSearch = !searchQuery || 
        `${emp.profile?.firstName} ${emp.profile?.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesDept && matchesSearch;
    });
  }, [employees, selectedDepartment, searchQuery]);

  const getDateStatus = (empId, day) => {
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const record = attendanceData[empId]?.[dateStr];
    
    if (record) {
      return record;
    }

    // Check if it's a weekend
    const date = new Date(selectedYear, selectedMonth, day);
    if (date.getDay() === 0 || date.getDay() === 6) {
      return { status: 'weekend' };
    }

    // Check if it's in the future
    if (date > new Date()) {
      return null;
    }

    return { status: 'absent' }; // Default to absent if no record
  };

  const getStatusBadge = (status) => {
    const statusConfig = ATTENDANCE_STATUSES.find(s => s.value === status);
    return statusConfig || ATTENDANCE_STATUSES[1]; // Default to absent
  };

  const handleCellClick = (empId, day) => {
    if (!editMode) return;
    setEditingCell({ empId, day });
  };

  const handleStatusChange = async (empId, day, newStatus, workHours = 8, overtime = 0) => {
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const date = new Date(selectedYear, selectedMonth, day);
    date.setHours(0, 0, 0, 0);
    const timestamp = date.getTime();

    try {
      const existingRecord = attendanceData[empId]?.[dateStr];

      if (existingRecord?.id) {
        // Update existing record
        await db.attendanceRecords.update(existingRecord.id, {
          status: newStatus,
          workHours: newStatus === 'present' ? workHours : (newStatus === 'half_day' ? workHours / 2 : 0),
          overtime: newStatus === 'present' ? overtime : 0,
          updatedAt: Date.now()
        });
      } else {
        // Create new record
        const checkIn = new Date(date);
        checkIn.setHours(9, 0, 0, 0);
        const checkOut = new Date(date);
        checkOut.setHours(17 + overtime, 0, 0, 0);

        await db.attendanceRecords.add({
          id: uuidv4(),
          userId: empId,
          date: timestamp,
          checkIn: newStatus === 'present' || newStatus === 'half_day' ? checkIn.getTime() : null,
          checkOut: newStatus === 'present' ? checkOut.getTime() : null,
          status: newStatus,
          workHours: newStatus === 'present' ? workHours : (newStatus === 'half_day' ? workHours / 2 : 0),
          overtime: newStatus === 'present' ? overtime : 0,
          createdAt: Date.now()
        });
      }

      setEditingCell(null);
      await loadData(); // Refresh data
      toast.success('Attendance updated');
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error('Failed to update attendance');
    }
  };

  const handleBulkEdit = async () => {
    if (bulkEditData.selectedEmployees.length === 0 || bulkEditData.selectedDates.length === 0) {
      toast.error('Please select employees and dates');
      return;
    }

    try {
      for (const empId of bulkEditData.selectedEmployees) {
        for (const day of bulkEditData.selectedDates) {
          await handleStatusChange(empId, day, bulkEditData.status);
        }
      }
      setShowBulkEditDialog(false);
      setBulkEditData({
        status: 'present',
        applyToAll: false,
        selectedEmployees: [],
        selectedDates: []
      });
      toast.success('Bulk attendance update completed');
    } catch (error) {
      toast.error('Failed to complete bulk update');
    }
  };

  const calculateEmployeeStats = (empId) => {
    const empData = attendanceData[empId] || {};
    let present = 0, absent = 0, leave = 0, halfDay = 0, overtime = 0;
    
    for (const record of Object.values(empData)) {
      if (record.status === 'present') present++;
      if (record.status === 'absent') absent++;
      if (record.status === 'leave') leave++;
      if (record.status === 'half_day') halfDay++;
      overtime += parseFloat(record.overtime || 0);
    }

    // Count working days passed
    const today = new Date();
    let workingDaysPassed = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(selectedYear, selectedMonth, d);
      if (date > today) break;
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        workingDaysPassed++;
      }
    }

    const attendanceRate = workingDaysPassed > 0 
      ? Math.round((present + halfDay * 0.5) / workingDaysPassed * 100) 
      : 0;

    return { present, absent, leave, halfDay, overtime: overtime.toFixed(1), attendanceRate, workingDaysPassed };
  };

  const viewEmployeeDetail = (emp) => {
    setSelectedEmployee(emp);
    setShowEmployeeDetailModal(true);
  };

  const exportToCSV = () => {
    let csv = 'Employee,';
    
    // Header row with dates
    for (let d = 1; d <= daysInMonth; d++) {
      csv += `${d},`;
    }
    csv += 'Present,Absent,Leave,Overtime,Rate\n';

    // Data rows
    for (const emp of filteredEmployees) {
      const stats = calculateEmployeeStats(emp.id);
      csv += `"${emp.profile?.firstName} ${emp.profile?.lastName}",`;
      
      for (let d = 1; d <= daysInMonth; d++) {
        const record = getDateStatus(emp.id, d);
        const status = record?.status?.charAt(0).toUpperCase() || '-';
        csv += `${status},`;
      }
      
      csv += `${stats.present},${stats.absent},${stats.leave},${stats.overtime},${stats.attendanceRate}%\n`;
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${MONTHS[selectedMonth]}_${selectedYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Attendance exported to CSV');
  };

  const renderCalendarCell = (emp, day) => {
    const record = getDateStatus(emp.id, day);
    const date = new Date(selectedYear, selectedMonth, day);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const isFuture = date > new Date();
    const isEditing = editingCell?.empId === emp.id && editingCell?.day === day;

    if (isEditing) {
      return (
        <td key={day} className="p-1 border">
          <Select
            defaultValue={record?.status || 'absent'}
            onValueChange={(value) => handleStatusChange(emp.id, day, value)}
          >
            <SelectTrigger className="h-8 w-full text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ATTENDANCE_STATUSES.filter(s => s.value !== 'weekend').map(status => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
      );
    }

    const statusBadge = getStatusBadge(record?.status);
    
    return (
      <td 
        key={day} 
        className={`p-1 border text-center cursor-pointer hover:bg-gray-50 transition-colors ${
          isWeekend ? 'bg-gray-50' : ''
        } ${isFuture ? 'opacity-50' : ''}`}
        onClick={() => !isWeekend && !isFuture && handleCellClick(emp.id, day)}
        title={`${emp.profile?.firstName}: ${statusBadge.label}${record?.overtime ? ` (+${record.overtime}h OT)` : ''}`}
      >
        <span className={`inline-block w-6 h-6 rounded-full text-xs leading-6 ${statusBadge.color}`}>
          {record?.status === 'present' ? '✓' : 
           record?.status === 'absent' ? '✗' : 
           record?.status === 'half_day' ? '½' :
           record?.status === 'leave' ? 'L' :
           record?.status === 'holiday' ? 'H' :
           isWeekend ? '-' : '?'}
        </span>
      </td>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monthly Attendance Management</h1>
          <p className="text-gray-500 mt-1">
            Manage and edit employee attendance records for payroll processing
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={editMode ? "default" : "outline"} 
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? <Save className="h-4 w-4 mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
            {editMode ? 'Save Changes' : 'Edit Mode'}
          </Button>
          <Button variant="outline" onClick={() => setShowBulkEditDialog(true)}>
            <Users className="h-4 w-4 mr-2" />
            Bulk Edit
          </Button>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Employees</p>
                <p className="text-2xl font-bold">{summary.totalEmployees}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Attendance</p>
                <p className="text-2xl font-bold">{summary.avgAttendance}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Present Days</p>
                <p className="text-2xl font-bold text-emerald-600">{summary.totalPresent}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Absent Days</p>
                <p className="text-2xl font-bold text-red-600">{summary.totalAbsent}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Leave Days</p>
                <p className="text-2xl font-bold text-blue-600">{summary.totalLeave}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Overtime (hrs)</p>
                <p className="text-2xl font-bold text-purple-600">{summary.totalOvertime}</p>
              </div>
              <Clock className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Month/Year Navigation */}
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => {
                  if (selectedMonth === 0) {
                    setSelectedMonth(11);
                    setSelectedYear(selectedYear - 1);
                  } else {
                    setSelectedMonth(selectedMonth - 1);
                  }
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex gap-2">
                <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month, idx) => (
                      <SelectItem key={idx} value={String(idx)}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => {
                  if (selectedMonth === 11) {
                    setSelectedMonth(0);
                    setSelectedYear(selectedYear + 1);
                  } else {
                    setSelectedMonth(selectedMonth + 1);
                  }
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Department Filter */}
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-gray-400" />
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.filter(d => d !== 'all').map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 px-2">
        {ATTENDANCE_STATUSES.map(status => (
          <div key={status.value} className="flex items-center gap-2">
            <span className={`w-4 h-4 rounded-full ${status.color.split(' ')[0]}`}></span>
            <span className="text-sm text-gray-600">{status.label}</span>
          </div>
        ))}
      </div>

      {/* Attendance Grid */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-white z-10 min-w-[200px]">Employee</TableHead>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const date = new Date(selectedYear, selectedMonth, i + 1);
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  return (
                    <TableHead 
                      key={i + 1} 
                      className={`text-center text-xs p-1 min-w-[40px] ${isWeekend ? 'bg-gray-100' : ''}`}
                    >
                      <div>{dayName}</div>
                      <div className="font-bold">{i + 1}</div>
                    </TableHead>
                  );
                })}
                <TableHead className="text-center">Stats</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.map(emp => {
                const stats = calculateEmployeeStats(emp.id);
                return (
                  <TableRow key={emp.id}>
                    <TableCell className="sticky left-0 bg-white z-10 font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold text-sm">
                          {emp.profile?.firstName?.[0]}{emp.profile?.lastName?.[0]}
                        </div>
                        <div>
                          <div className="font-medium text-sm">
                            {emp.profile?.firstName} {emp.profile?.lastName}
                          </div>
                          <div className="text-xs text-gray-500">{emp.profile?.department}</div>
                        </div>
                      </div>
                    </TableCell>
                    {Array.from({ length: daysInMonth }, (_, i) => renderCalendarCell(emp, i + 1))}
                    <TableCell className="text-center">
                      <div className="flex flex-col items-center gap-1">
                        <Badge 
                          variant="outline" 
                          className={stats.attendanceRate >= 90 ? 'bg-emerald-50 text-emerald-700' : 
                            stats.attendanceRate >= 75 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}
                        >
                          {stats.attendanceRate}%
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {stats.present}P / {stats.absent}A / {stats.leave}L
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => viewEmployeeDetail(emp)}>
                            <FileText className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setBulkEditData(prev => ({
                              ...prev,
                              selectedEmployees: [emp.id]
                            }));
                            setShowBulkEditDialog(true);
                          }}>
                            <Edit className="h-4 w-4 mr-2" />
                            Quick Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEditDialog} onOpenChange={setShowBulkEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bulk Attendance Edit</DialogTitle>
            <DialogDescription>
              Update attendance for multiple employees or dates at once
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Status Selection */}
            <div className="space-y-2">
              <Label>Attendance Status</Label>
              <Select 
                value={bulkEditData.status} 
                onValueChange={(v) => setBulkEditData(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ATTENDANCE_STATUSES.filter(s => s.value !== 'weekend').map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Employee Selection */}
            <div className="space-y-2">
              <Label>Select Employees</Label>
              {/* Employee Search */}
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search employees..."
                  value={bulkEditData.searchQuery || ''}
                  onChange={(e) => setBulkEditData(prev => ({ ...prev, searchQuery: e.target.value }))}
                  className="pl-9"
                />
              </div>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                <div className="flex items-center gap-2 pb-2 border-b">
                  <Checkbox
                    checked={bulkEditData.selectedEmployees.length === filteredEmployees.filter(e => 
                      !bulkEditData.searchQuery || 
                      `${e.profile?.firstName} ${e.profile?.lastName}`.toLowerCase().includes(bulkEditData.searchQuery?.toLowerCase() || '')
                    ).length && bulkEditData.selectedEmployees.length > 0}
                    onCheckedChange={(checked) => {
                      const searchFilteredEmps = filteredEmployees.filter(e => 
                        !bulkEditData.searchQuery || 
                        `${e.profile?.firstName} ${e.profile?.lastName}`.toLowerCase().includes(bulkEditData.searchQuery?.toLowerCase() || '')
                      );
                      setBulkEditData(prev => ({
                        ...prev,
                        selectedEmployees: checked ? searchFilteredEmps.map(e => e.id) : []
                      }));
                    }}
                  />
                  <span className="font-medium text-sm">Select All {bulkEditData.searchQuery ? 'Filtered' : ''}</span>
                </div>
                {filteredEmployees
                  .filter(emp => 
                    !bulkEditData.searchQuery || 
                    `${emp.profile?.firstName} ${emp.profile?.lastName}`.toLowerCase().includes(bulkEditData.searchQuery?.toLowerCase() || '') ||
                    emp.email.toLowerCase().includes(bulkEditData.searchQuery?.toLowerCase() || '')
                  )
                  .map(emp => (
                  <div key={emp.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={bulkEditData.selectedEmployees.includes(emp.id)}
                      onCheckedChange={(checked) => {
                        setBulkEditData(prev => ({
                          ...prev,
                          selectedEmployees: checked 
                            ? [...prev.selectedEmployees, emp.id]
                            : prev.selectedEmployees.filter(id => id !== emp.id)
                        }));
                      }}
                    />
                    <span className="text-sm">
                      {emp.profile?.firstName} {emp.profile?.lastName}
                    </span>
                    <span className="text-xs text-gray-400">({emp.profile?.department || 'No Dept'})</span>
                  </div>
                ))}
                {filteredEmployees.filter(emp => 
                  !bulkEditData.searchQuery || 
                  `${emp.profile?.firstName} ${emp.profile?.lastName}`.toLowerCase().includes(bulkEditData.searchQuery?.toLowerCase() || '')
                ).length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-2">No employees match your search</p>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {bulkEditData.selectedEmployees.length} employee(s) selected
              </p>
            </div>

            {/* Date Selection */}
            <div className="space-y-2">
              <Label>Select Dates</Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const date = new Date(selectedYear, selectedMonth, day);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    const isSelected = bulkEditData.selectedDates.includes(day);
                    
                    return (
                      <button
                        key={day}
                        className={`p-2 text-sm rounded ${
                          isWeekend ? 'bg-gray-100 text-gray-400 cursor-not-allowed' :
                          isSelected ? 'bg-blue-600 text-white' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        disabled={isWeekend}
                        onClick={() => {
                          if (isWeekend) return;
                          setBulkEditData(prev => ({
                            ...prev,
                            selectedDates: isSelected
                              ? prev.selectedDates.filter(d => d !== day)
                              : [...prev.selectedDates, day]
                          }));
                        }}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkEdit}>
              Apply Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Detail Modal */}
      <Dialog open={showEmployeeDetailModal} onOpenChange={setShowEmployeeDetailModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {selectedEmployee?.profile?.firstName} {selectedEmployee?.profile?.lastName} - Attendance Details
            </DialogTitle>
            <DialogDescription>
              {MONTHS[selectedMonth]} {selectedYear}
            </DialogDescription>
          </DialogHeader>

          {selectedEmployee && (
            <div className="space-y-4">
              {(() => {
                const stats = calculateEmployeeStats(selectedEmployee.id);
                return (
                  <>
                    {/* Stats Overview */}
                    <div className="grid grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-4 text-center">
                          <p className="text-2xl font-bold text-emerald-600">{stats.present}</p>
                          <p className="text-xs text-gray-500">Present</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 text-center">
                          <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
                          <p className="text-xs text-gray-500">Absent</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 text-center">
                          <p className="text-2xl font-bold text-blue-600">{stats.leave}</p>
                          <p className="text-xs text-gray-500">Leave</p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 text-center">
                          <p className="text-2xl font-bold text-purple-600">{stats.overtime}h</p>
                          <p className="text-xs text-gray-500">Overtime</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Attendance Rate */}
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex justify-between mb-2">
                          <span className="text-sm text-gray-600">Attendance Rate</span>
                          <span className="font-bold">{stats.attendanceRate}%</span>
                        </div>
                        <Progress value={stats.attendanceRate} className="h-2" />
                        <p className="text-xs text-gray-500 mt-2">
                          {stats.present} present out of {stats.workingDaysPassed} working days
                        </p>
                      </CardContent>
                    </Card>

                    {/* Daily Breakdown */}
                    <div>
                      <Label className="mb-2 block">Daily Breakdown</Label>
                      <div className="border rounded-lg max-h-48 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Check In</TableHead>
                              <TableHead>Check Out</TableHead>
                              <TableHead>Hours</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {Array.from({ length: daysInMonth }, (_, i) => {
                              const day = i + 1;
                              const record = getDateStatus(selectedEmployee.id, day);
                              const date = new Date(selectedYear, selectedMonth, day);
                              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                              
                              if (isWeekend) return null;
                              
                              const statusBadge = getStatusBadge(record?.status);
                              
                              return (
                                <TableRow key={day}>
                                  <TableCell>
                                    {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                  </TableCell>
                                  <TableCell>
                                    <Badge className={statusBadge.color}>{statusBadge.label}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    {record?.checkIn ? new Date(record.checkIn).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {record?.checkOut ? new Date(record.checkOut).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {record?.workHours ? `${parseFloat(record.workHours).toFixed(1)}h` : '-'}
                                    {record?.overtime > 0 && (
                                      <span className="text-purple-600 ml-1">(+{record.overtime}h OT)</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeeDetailModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
